/**
 * Copyright ©2023 Drivepoint
 */

import React, {createRef, useEffect, useRef, useState} from "react";
import {ListItemButton, ListItemIcon, ListItemText, Tooltip} from "@mui/material";
import {Group, Lock, OpenInNewOutlined} from "@mui/icons-material";
import useStateChange from "@hooks/useStateChange";
import BainbridgeUser from "@services/user/BainbridgeUser";
import Logger from "@utilities/logger/Logger";
import usePage from "@hooks/usePage";
import "./NavigationPageItem.less";

const logger = Logger.logger;

export type NavigationPageItemProps = {
  item: any;
  open?: boolean;
  currentPage?: any;
  onPageContextMenu: (event: any, page: any) => void;
  onPageClick: (event: any, page: any) => void;
};

export default function NavigationPageItem(props: NavigationPageItemProps): any {

  const ref = useRef<HTMLElement>();
  const user = useStateChange<BainbridgeUser>("user");
  const page = usePage();

  const [truncated, setTruncated] = useState<boolean>(false);

  useEffect(() => {
    if (!ref.current) { return; }
    if (!props.open) {
      setTruncated(false);
      return;
    }
    setTimeout(() => {
      const element: HTMLElement = ref.current?.getElementsByClassName("MuiListItemText-primary")?.[0] as HTMLElement;
      if (!element) { return; }
      setTruncated(element.offsetWidth < element.scrollWidth);
    }, 500);
  }, [ref.current, props, open]);

  function renderVisibilityIcon(): any {
    switch (props.item.visibility) {
      case "company": return <ListItemIcon className="navigation-section-page-visibility-icon"><Group /></ListItemIcon>;
      case "user": return <ListItemIcon className="navigation-section-page-visibility-icon"><Lock /></ListItemIcon>;
    }
  }

  function renderNavigationIcon(): any {
    if ((user || props.item.public) && props.item.external) {
      return <ListItemIcon className="navigation-section-page-navigation-icon"><OpenInNewOutlined /></ListItemIcon>;
    }
  }

  function renderIcon(): any {
    if (props.open === false) {
      return <Tooltip title={props.item.name} arrow placement="right">
        <div>
          <ListItemIcon className="navigation-section-page-icon">{props.item.icon && <props.item.icon />}</ListItemIcon>
        </div>
      </Tooltip>;
    }
    return <ListItemIcon className="navigation-section-page-icon">{props.item.icon && <props.item.icon />}</ListItemIcon>;
  }

  function renderName(): any {
    if (truncated) {
      return <Tooltip title={props.item.name} arrow placement="right">
        <ListItemText className="navigation-section-page-text" primary={props.item.name} ref={ref} />
      </Tooltip>;
    } else {
      return <ListItemText className="navigation-section-page-text" primary={props.item.name} ref={ref} />
    }
  }

  const classes = ["navigation-section-page"];
  if (props.item.id === page.current?.id) { classes.push("highlight"); }

  return <ListItemButton
    className={`${classes.join(" ")}`}
    onContextMenu={(event: any) => props.onPageContextMenu(event, props.item)}
    onClick={(event: any) => props.onPageClick(event, props.item)}
  >
    {renderIcon()}
    {renderVisibilityIcon()}
    {renderName()}
    {renderNavigationIcon()}
  </ListItemButton>;

}
